import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';

import InterstateBolsContainer from '../../containers/billing/InterstateBols';
import AuthUserContainer from '../../containers/AuthUser';

import { getSharedStyles } from '../../themes';
import EnhancedTable from '../../components/table/EnhancedTable';
import { Typography } from '@material-ui/core';

import ResourcePopup from '../../components/Dialogs/ResourcePopup';
import InterstateBolUploadDialog from '../../components/Dialogs/InterstateBolUploadDialog';
import { grey, blue, orange } from '@material-ui/core/colors';
import BolLinkedStatus, { LINKED_STATUS_ENUM } from '../../components/billing/BolLinkedStatus';

import invoiceIcon from '../../images/invoicePdf.svg';

const UPLOAD_BOL_ENABLED = true;

const INTAKE_TYPE_ENUM = {
	manual: 'manual',
	emailIngestion: 'emailIngestion',
};

const intakeTypeToDisplayMap = {
	[INTAKE_TYPE_ENUM.manual]: 'Manual',
	[INTAKE_TYPE_ENUM.emailIngestion]: 'Email Ingestion',
};

const intakeTypeToColorMap = {
	[INTAKE_TYPE_ENUM.manual]: grey[500],
	[INTAKE_TYPE_ENUM.emailIngestion]: blue[500],
};

function intakeTypeToDisplay(intakeType) {
	return intakeTypeToDisplayMap[intakeType] || 'Manual';
}

const styles = (theme) => ({
	...getSharedStyles(theme),
	uploadButton: {
		borderRadius: 100,
	},
	invoiceIcon: {
		display: 'inline-block',
		width: 40,
		height: 48,
		backgroundImage: `url('${invoiceIcon}')`,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		position: 'relative',
		flexShrink: 0,
		cursor: 'pointer',
	},
	handwrittenBadge: {
		display: 'inline-block',
		marginLeft: 6,
		padding: '1px 6px',
		borderRadius: 8,
		backgroundColor: orange[100],
		color: orange[900],
		fontSize: 11,
		fontWeight: 'bold',
		whiteSpace: 'nowrap',
	},
});

const columns = [
	{
		sortId: 'interstateBolId',
		displayId: 'interstateBolId',
		label: 'ID',
	},
	{
		sortId: 'bolNumber',
		displayId: 'bolNumber',
		label: 'BOL #',
	},
	{
		sortId: 'loadingNumber',
		displayId: 'pearsonIdDisplay',
		label: 'Pearson ID',
	},
	{
		sortId: 'railcarNumber1',
		displayId: 'railcarDisplay',
		label: 'Railcar #',
	},
	{
		sortId: 'carrierRaw',
		displayId: 'carrierDisplay',
		label: 'Carrier / Truck',
	},
	{
		sortId: 'startDateTime',
		displayId: 'bolDateDisplay',
		label: 'BOL Date',
	},
	{
		sortId: 'netGallons',
		displayId: 'gallonsDisplay',
		label: 'Gallons',
	},
	{
		noSort: true,
		sortId: 'bolPdfResourceId',
		displayId: 'bolPdfDisplay',
		label: 'PDF',
	},
	{
		sortId: 'intakeType',
		displayId: 'intakeDisplay',
		label: 'Intake',
	},
	{
		sortId: 'linkedStationDeliveryId',
		displayId: 'linkedStationDeliveryDisplay',
		label: 'Linked Delivery',
	},
	{
		sortId: 'linkedStatus',
		displayId: 'linkedStatusDisplay',
		label: 'Linked Status',
	},
];

class InterstateBols extends Component {
	constructor(props) {
		super(props);

		this.state = {
			order: 'desc',
			orderBy: 'interstateBolId',
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
			isUploadOpen: false,
			reparsingInterstateBolId: null,
		};
	}

	componentDidMount() {
		const { interstateBols } = this.props;

		if (!interstateBols) {
			this.refreshTable();
		} else {
			this.formatInterstateBols();
		}
	}

	componentDidUpdate(prevProps) {
		const { interstateBols } = this.props;
		if (prevProps.interstateBols !== interstateBols) {
			this.formatInterstateBols();
		}
	}

	refreshTable = () => {
		this.props.interstateBolSearch({
			afterDateTime: moment().subtract(2, 'months').format('YYYY-MM-DD'),
		});
	};

	formatInterstateBols = () => {
		const { classes, interstateBols, stationMap, isDwp } = this.props;
		const { reparsingInterstateBolId } = this.state;

		const rows = [];
		(interstateBols || []).forEach((interstateBol) => {
			const { interstateBolId, startDateTime, dateCreated } = interstateBol;

			interstateBol.key = interstateBolId;
			interstateBol.bolDateDisplay = startDateTime ? moment(startDateTime).format('M/D/YY') : '-';
			interstateBol.dateCreatedFormatted = dateCreated
				? moment(dateCreated).format('M/D/YY h:mm a')
				: '-';

			// The Pearson ID is our stationDeliveryId — flag handwritten ones, since
			// a misread there is the main link risk.
			interstateBol.pearsonIdDisplay = (
				<span style={{ whiteSpace: 'nowrap' }}>
					{interstateBol.loadingNumber || '-'}
					{!!interstateBol.pearsonIdHandwritten && (
						<span className={classes.handwrittenBadge}>Handwritten</span>
					)}
				</span>
			);

			// Up to three loading sources — show each railcar with its per-source
			// net/gross gallons on its own line.
			interstateBol.railcarDisplay = (
				<div>
					{[1, 2, 3].map((n) => {
						const railcarNumber = interstateBol[`railcarNumber${n}`];
						if (!railcarNumber) {
							return n === 1 ? <div key={n}>-</div> : null;
						}
						const net = interstateBol[`netGallons${n}`];
						const gross = interstateBol[`grossGallons${n}`];
						return (
							<div style={{ whiteSpace: 'nowrap' }} key={n}>
								{railcarNumber}
								{!_.isNil(net) ? ` (${net} / ${gross} gr)` : ''}
							</div>
						);
					})}
				</div>
			);

			interstateBol.carrierDisplay = (
				<div>
					<div>{interstateBol.carrierRaw || '-'}</div>
					{(interstateBol.truckNumber || interstateBol.trailerNumber) && (
						<div style={{ whiteSpace: 'nowrap' }}>
							Truck {interstateBol.truckNumber || '?'} / {interstateBol.trailerNumber || '?'}
						</div>
					)}
				</div>
			);

			interstateBol.gallonsDisplay = (
				<div style={{ whiteSpace: 'nowrap' }}>
					<div>{_.isNil(interstateBol.netGallons) ? '-' : `${interstateBol.netGallons} net`}</div>
					{!_.isNil(interstateBol.grossGallons) && <div>{interstateBol.grossGallons} gross</div>}
				</div>
			);

			interstateBol.bolPdfDisplay = interstateBol.bolPdfResourceId ? (
				<div
					className={classes.invoiceIcon}
					onClick={() => {
						this.handlePdfClick(interstateBol.bolPdfResourceId);
					}}
				/>
			) : (
				'-'
			);

			// DEV-only (dwp): re-run the parser + link on a row that failed under an
			// older parser. Rows that are already railcar-linked are refused server-side.
			const canReparse = isDwp && interstateBol.linkedStatus !== LINKED_STATUS_ENUM.railcarLinked;
			interstateBol.linkedStatusDisplay = (
				<div>
					<BolLinkedStatus
						linkedStatus={interstateBol.linkedStatus}
						message={interstateBol.linkedErrorMessage}
					/>
					{canReparse && (
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							style={{ marginTop: 6, whiteSpace: 'nowrap' }}
							disabled={reparsingInterstateBolId === interstateBolId}
							onClick={() => {
								this.handleReparse(interstateBolId);
							}}
						>
							{reparsingInterstateBolId === interstateBolId ? 'Reparsing...' : 'DEV Only: Reparse'}
						</Button>
					)}
				</div>
			);

			interstateBol.intakeDisplay = (
				<div>
					<div
						style={{ color: intakeTypeToColorMap[interstateBol.intakeType], fontWeight: 'bold' }}
					>
						Type: {intakeTypeToDisplay(interstateBol.intakeType)}
					</div>
					<div>Date Created: {interstateBol.dateCreatedFormatted}</div>
				</div>
			);

			if (interstateBol.linkedStationDeliveryId) {
				const station = stationMap[interstateBol.linkedStationId] || { number: 'Missing' };
				// One BOL can serve BOTH legs of a tank split — show the second leg
				// and the per-leg gallon split when present.
				const station2 = interstateBol.linkedStationId2
					? stationMap[interstateBol.linkedStationId2] || { number: 'Missing' }
					: null;
				interstateBol.linkedStationDeliveryDisplay = (
					<div>
						<div style={{ whiteSpace: 'nowrap' }}>
							Station Delivery #
							<a
								href={`/logistics/stationDelivery/update/${interstateBol.linkedStationDeliveryId}`}
								style={{ textDecoration: 'none', paddingTop: 5, paddingLeft: 5 }}
								target="_blank"
								rel="noreferrer noopener"
							>
								<strong>{interstateBol.linkedStationDeliveryId}</strong>
							</a>
						</div>
						<div>
							Station: {station.number} / Tank: {interstateBol.linkedTankNumber}
							{station2 && !_.isNil(interstateBol.linkedLegNetGallons1)
								? ` (${interstateBol.linkedLegNetGallons1})`
								: ''}
						</div>
						{station2 && (
							<div>
								Station: {station2.number} / Tank: {interstateBol.linkedTankNumber2}
								{!_.isNil(interstateBol.linkedLegNetGallons2)
									? ` (${interstateBol.linkedLegNetGallons2})`
									: ''}
							</div>
						)}
					</div>
				);
			}

			rows.push(interstateBol);
		});
		this.setState({ rows });
	};

	handleReparse = (interstateBolId) => {
		const { interstateBolReparse } = this.props;
		this.setState({ reparsingInterstateBolId: interstateBolId }, this.formatInterstateBols);
		interstateBolReparse({
			params: { interstateBolId },
			// apiFetch already surfaces errors via ERROR_EVENT; just refresh either
			// way so the row's new parse/link status shows.
			callback: () => {
				this.setState({ reparsingInterstateBolId: null }, this.formatInterstateBols);
				this.refreshTable();
			},
		});
	};

	handleOpenUpload = () => {
		this.setState({ isUploadOpen: true });
	};

	handleCloseUpload = () => {
		this.setState({ isUploadOpen: false });
	};

	handlePdfClick = (resourceId) => {
		this.setState({
			resourceIdsPoppedUp: [resourceId],
			isResourcePopupOpen: true,
		});
	};

	handleCloseResourcePopup = () => {
		this.setState({
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
		});
	};

	render() {
		const { classes, resourceMap, interstateBolUpload, showErrors } = this.props;
		const { rows, order, orderBy } = this.state;

		if (!rows) {
			return <div>Loading the InterState BOLs...</div>;
		}

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Grid container spacing={3} alignItems="center">
						<Grid item xs={8}>
							<Typography variant="h5" align="left" gutterBottom className={classes.title}>
								<span>InterState BOLs</span>
							</Typography>
						</Grid>
						<Grid item xs={4} style={{ textAlign: 'right' }}>
							<Button
								variant="outlined"
								color="primary"
								className={classes.uploadButton}
								style={{ marginRight: 10 }}
								onClick={this.refreshTable}
							>
								Refresh
							</Button>
							{UPLOAD_BOL_ENABLED && (
								<Button
									variant="contained"
									color="primary"
									className={classes.uploadButton}
									onClick={this.handleOpenUpload}
								>
									Upload BOL
								</Button>
							)}
						</Grid>
					</Grid>

					<Paper className={classes.paper}>
						<EnhancedTable
							columns={columns}
							rows={rows}
							defaultOrder={order}
							defaultOrderBy={orderBy}
						/>

						<ResourcePopup
							open={this.state.isResourcePopupOpen}
							onClose={this.handleCloseResourcePopup}
							resourceIds={this.state.resourceIdsPoppedUp}
							resourceMap={resourceMap}
						/>
					</Paper>

					{UPLOAD_BOL_ENABLED && (
						<InterstateBolUploadDialog
							open={this.state.isUploadOpen}
							onClose={this.handleCloseUpload}
							uploadBol={interstateBolUpload}
							showErrors={showErrors}
							onAfterSave={this.refreshTable}
						/>
					)}
				</Grid>
			</Grid>
		);
	}
}

InterstateBols.propTypes = {
	classes: PropTypes.object.isRequired,
	interstateBolSearch: PropTypes.func.isRequired,
	interstateBolUpload: PropTypes.func.isRequired,
	interstateBolReparse: PropTypes.func.isRequired,
	interstateBols: PropTypes.array,
	isDwp: PropTypes.bool,

	showErrors: PropTypes.func.isRequired,
	resourceMap: PropTypes.object,
	stationMap: PropTypes.object,
	terminalMap: PropTypes.object,

	history: PropTypes.object,
	theme: PropTypes.object.isRequired,
	authUser: PropTypes.object,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	AuthUserContainer,
	InterstateBolsContainer,
])(InterstateBols);
